import type { Customer } from '../../../api/customer';
import type { Size } from '../../../api/size';
import { VestmentCategory } from '../../enum/vestmentCategory';
import { VestmentType } from '../../enum/vestmentType';
import { getAlbFeatures } from '../alb';
import { formatMeasurement, toMeasurementNumber } from '../measurement';
import { type SizeMeasurementField, isChasubleCategory } from '../size';

export type MeasurementSources = {
  customer?: Customer | null;
  size?: Size | null;
  vestmentType?: VestmentType | null;
  vestmentCategory?: VestmentCategory | null;
};

export function resolveMeasurement(
  field: SizeMeasurementField,
  sources: MeasurementSources & { formValue?: string }
): string {
  const fromForm = toMeasurementNumber(sources.formValue);
  const raw = fromForm ?? resolveRawMeasurement(field, sources);
  return formatMeasurement(raw) ?? '-';
}

export function resolveRawMeasurement(
  field: SizeMeasurementField,
  sources: MeasurementSources
): number | undefined {
  const { customer, size, vestmentType, vestmentCategory } = sources;
  if (isChasubleCategory(vestmentType, vestmentCategory)) {
    return vestmentType === VestmentType.ChasubleAlb
      ? resolveChasubleAlbMeasurement(field, customer, size)
      : resolveChasubleMeasurement(field, customer, size);
  }
  return field === 'length'
    ? resolveLength(customer, size, vestmentType)
    : fromCustomerOrSize(customer, size, field);
}

function resolveLength(
  customer: Customer | null | undefined,
  size: Size | null | undefined,
  vestmentType?: VestmentType | null
): number | undefined {
  switch (vestmentType) {
    case VestmentType.StoleFlatPriest:
      return firstWithOffset([
        [readField(customer, 'chasubleLength'), 7],
        [readField(customer, 'length'), 2],
        [readField(size, 'length'), 0],
      ]);
    case VestmentType.StoleStandingBackPriest:
      return firstWithOffset([
        [readField(customer, 'chasubleLength'), 5],
        [readField(customer, 'length'), 0],
        [readField(size, 'length'), 0],
      ]);
    case VestmentType.StolePastoralPriest:
    case VestmentType.StoleDeacon:
    case VestmentType.StoleV1Ordinary:
    case VestmentType.StoleV1Special:
    case VestmentType.StoleV1RomanOrdinary:
    case VestmentType.StoleV1RomanSpecial:
    case VestmentType.StoleV1DeaconStraight:
    case VestmentType.StoleV1DeaconLShaped:
      return fromCustomerOrSize(customer, size, 'length');
    default:
      return firstWithOffset([
        [readField(customer, 'length'), albLengthOffset(vestmentType)],
        [readField(size, 'length'), 0],
      ]);
  }
}

function albLengthOffset(vestmentType?: VestmentType | null): number {
  return getAlbFeatures(vestmentType)?.lengthOffset ?? 0;
}

function resolveChasubleMeasurement(
  field: SizeMeasurementField,
  customer: Customer | null | undefined,
  size: Size | null | undefined
): number | undefined {
  switch (field) {
    case 'length':
      return firstWithOffset([
        [readField(customer, 'chasubleLength'), 0],
        [readField(customer, 'length'), -5],
        [readField(size, 'length'), 0],
      ]);
    case 'sleeve':
      return firstWithOffset([
        [readField(customer, 'chasubleSleeve'), 0],
        [halfShoulderPlusSleeve(customer), -3],
        [readField(size, 'sleeve'), 0],
      ]);
    case 'neck':
      return firstWithOffset([
        [readField(customer, 'neck'), 1],
        [readField(size, 'neck'), 0],
      ]);
    default:
      return fromCustomerOrSize(customer, size, field);
  }
}

function resolveChasubleAlbMeasurement(
  field: SizeMeasurementField,
  customer: Customer | null | undefined,
  size: Size | null | undefined
): number | undefined {
  switch (field) {
    case 'length':
      return firstWithOffset([
        [readField(customer, 'length'), -2],
        [readField(customer, 'chasubleLength'), 3],
        [readField(size, 'length'), 3],
      ]);
    case 'sleeve':
      return firstWithOffset([
        [halfShoulderPlusSleeve(customer), -1],
        [readField(customer, 'chasubleSleeve'), 2],
        [readField(size, 'sleeve'), 2],
      ]);
    case 'neck':
      return firstWithOffset([
        [readField(customer, 'neck'), 1],
        [readField(size, 'neck'), 1],
      ]);
    default:
      return fromCustomerOrSize(customer, size, field);
  }
}

function halfShoulderPlusSleeve(
  customer: Customer | null | undefined
): number | undefined {
  const shoulder = readField(customer, 'shoulder');
  const sleeve = readField(customer, 'sleeve');
  return shoulder != null && sleeve != null ? shoulder / 2 + sleeve : undefined;
}

function firstWithOffset(
  candidates: readonly (readonly [value: number | undefined, offset: number])[]
): number | undefined {
  for (const [value, offset] of candidates) {
    if (value != null) {
      return value + offset;
    }
  }
  return undefined;
}

function fromCustomerOrSize(
  customer: Customer | null | undefined,
  size: Size | null | undefined,
  field: SizeMeasurementField
): number | undefined {
  return readField(customer, field) ?? readField(size, field);
}

export function hasMeasurementOverrides(
  fields: readonly SizeMeasurementField[],
  measurements: Partial<
    Record<SizeMeasurementField, string | number | null | undefined>
  >,
  sources: MeasurementSources
): boolean {
  const { size, customer, vestmentType, vestmentCategory } = sources;
  const hasBaseline = size != null || customer != null;
  if (!hasBaseline) {
    return false;
  }

  return fields.some((field) => {
    const actual = toMeasurementNumber(measurements[field]);
    if (actual == null) {
      return false;
    }

    const baseline = resolveRawMeasurement(field, {
      customer,
      size,
      vestmentType,
      vestmentCategory,
    });
    if (baseline == null) {
      return true;
    }

    return actual !== baseline;
  });
}

export function defaultPanelWidth(
  centerFinish?: string | number | null,
  designCenterFinish?: number | null
): number | undefined {
  const base =
    toMeasurementNumber(centerFinish) ??
    toMeasurementNumber(designCenterFinish);
  return base == null ? undefined : base + 2;
}

export function defaultPanelLength(
  sources: MeasurementSources & { lineItemLength?: string }
): number | undefined {
  const length =
    toMeasurementNumber(sources.lineItemLength) ??
    resolveRawMeasurement('length', sources);
  return length == null ? undefined : length + 3;
}

export function displayMeasurement(
  formValue?: string | number | null,
  fallback?: number | null
): string {
  return (
    formatMeasurement(toMeasurementNumber(formValue)) ??
    formatMeasurement(fallback) ??
    '-'
  );
}

export function getMeasurementPlaceholder(
  field: SizeMeasurementField,
  sources: MeasurementSources
): string | undefined {
  return formatMeasurement(resolveRawMeasurement(field, sources)) ?? undefined;
}

function readField(
  source:
    | Pick<Customer, SizeMeasurementField | 'chasubleLength' | 'chasubleSleeve'>
    | null
    | undefined,
  field: SizeMeasurementField | 'chasubleLength' | 'chasubleSleeve'
): number | undefined {
  return toMeasurementNumber(source?.[field]);
}
